import React, {Fragment, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Field} from 'formik';
import classnames from 'classnames';
import {RadioGroup} from 'formik-material-ui';
import FormControl from '../components/FormControl.jsx';
import FormGroup from '../components/FormGroup.jsx';
import FormControlLabel from '../components/FormControlLabel.jsx';
import Radio from '../components/Radio.jsx';
import styles from '../styles/styles.js';
import useFormikSchema from '../hooks/useFormikSchema.js';
import Checkbox from '../components/Checkbox.jsx';
import { FormLabel } from '@material-ui/core';
const {makeStyles} = styles;

const CheckboxGroup = ({field, form, meta, ...props}) => {
  const {options, value, disabled} = props;
  const dataTestId = props['data-testid'];
  const handleChange = useCallback(
      (option) => (event) => {
        if (event.target.checked) {
          form.setFieldValue(field.name, [...field.value, option.value]);
        } else {
          const list = [...field.value];
          const index = list.indexOf(option.value);
          if (index >= 0) {
            list.splice(index, 1);
          }
          form.setFieldValue(field.name, list);
        }
      },
      [options, field.value, field.name],
  );

  return (
    <Fragment>
      {
        options.map((option, index) => (
          <Checkbox
            color={'primary'}
            key={`${name}-${index}`}
            value={option.value}
            label={option.label}
            onChange={handleChange(option)}
            checked={!!value?.includes?.(option.value)}
            data-testid={`${dataTestId||'option'}-${index}`}
            disabled={disabled || option.disabled || false}
          />
        ))
      }
    </Fragment>
  );
};

const FormikRadioGroup = (props) => {
  const {
    disabled,
    value,
    name,
    label,
    options,
    labelClass,
    labelPlacement,
    alignment,
    schema,
    multiple,
    ariaLabel,
    legendClass,
  } = props;

  const dataTestId = props['data-testid'];
  const classes = useStyles();
  const {formattedLabel} = useFormikSchema(name, label, schema);

  return (
    <FormControl component={ 'fieldset' }>
      <FormGroup>
        <FormControlLabel
          className={classnames(labelClass, classes.label) }
          labelPlacement={labelPlacement }
          control={
            <Field
              disabled={ disabled }
              value={ value }
              data-testid={dataTestId}
              name={ name }
              options={options}
              component={ multiple ? CheckboxGroup : RadioGroup }
              row={alignment === 'horizontal'}
              aria-label={ariaLabel}
            >
              {
                !multiple && options.map((option, index) => (
                  <FormControlLabel
                    key={`${name}-${index}`}
                    value={option.value}
                    checked={
                      multiple ?
                        (value||[]).includes(option.value) :
                        value === option.value
                    }
                    control={
                      <Radio
                        data-testid={`${dataTestId||'option'}-${index}`}
                        disabled={disabled || option.disabled || false}
                      />
                    }
                    label={option.label} />
                ))
              }
            </Field>
          }
          label={
            <FormLabel
              component='legend'
              className={ classnames(classes.labelLegend, legendClass)}>
              {formattedLabel}
            </FormLabel>
          } />
      </FormGroup>
    </FormControl>
  );
};

FormikRadioGroup.propTypes = {
  'disabled': PropTypes.bool,
  'value': PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  'multiple': PropTypes.bool,
  'name': PropTypes.string.isRequired,
  'options': PropTypes.array.isRequired,
  'data-testid': PropTypes.string,
  'label': PropTypes.string,
  'labelClass': PropTypes.string,
  'legendClass': PropTypes.string,
  'labelPlacement': PropTypes.string,
  'schema': PropTypes.object,
  'alignment': PropTypes.oneOf(['horizontal', 'vertical']),
};

FormikRadioGroup.defaultProps = {
  label: '',
  labelClass: '',
  labelPlacement: 'top',
  alignment: 'vertical',
};

const useStyles = makeStyles((theme) => ({
  label: {
    alignItems: 'flex-start',
  },
  labelLegend: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: 20,
  },
}));

export default FormikRadioGroup;
